import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { truncate } from "lodash";

import type { DetailsValues } from "./PathDetailsStep";
import type { SettingsValues } from "./PathSettingsStep";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReviewStepValues {
  details?: Partial<DetailsValues>;
  content?: {
    totalPoints?: number;
    selectedLessons?: { name?: string }[];
    orderedLessons?: { name?: string }[];
  };
  settings?: Partial<SettingsValues>;
}

interface ValidationErrors {
  details?: {
    name?: { hasError: boolean; message: string };
    description?: { hasError: boolean; message: string };
    level?: { hasError: boolean; message: string };
  };
  content?: { hasError: boolean; message: string };
  settings?: { dates?: { hasError: boolean; message: string } };
}

interface ReviewStepProps {
  values: ReviewStepValues;
  onStepChange: (step: number) => void;
  validationErrors: ValidationErrors;
  contentTypeLabel: string;
  isActivityPath?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ReviewStep = ({
  values,
  onStepChange,
  validationErrors,
  contentTypeLabel,
  isActivityPath = false,
}: ReviewStepProps) => {
  return (
    <div className="grid xl:grid-cols-3 gap-4">
      {/* Details */}
      <Card className={classNames(themeClasses.bgElevation2, "min-h-52")}>
        <div className="mb-2 flex items-center">
          <h4 className="font-semibold text-xl">1. {contentTypeLabel} Details</h4>
          {(validationErrors.details?.name?.hasError ||
            validationErrors.details?.description?.hasError ||
            (contentTypeLabel === "Path" && validationErrors.details?.level?.hasError)) && (
            <span className="ml-2">
              <i
                className={`pi pi-info-circle ${themeClasses.iconError}`}
                style={{ fontSize: "1rem" }}
                id="details-validation-error"
              />
              <Tooltip target="#details-validation-error" position="right">
                <ul className="list-disc pl-4">
                  {validationErrors.details?.name?.hasError && (
                    <li>{validationErrors.details.name.message}</li>
                  )}
                  {validationErrors.details?.description?.hasError && (
                    <li>{validationErrors.details.description.message}</li>
                  )}
                  {contentTypeLabel === "Path" && validationErrors.details?.level?.hasError && (
                    <li>{validationErrors.details.level?.message}</li>
                  )}
                </ul>
              </Tooltip>
            </span>
          )}
          <Button
            label="Edit"
            onClick={() => onStepChange(0)}
            link
            size="small"
            className="ml-[10px]"
          />
        </div>
        <div className="space-y-4">
          {contentTypeLabel === "Path" && (
            <div className="flex justify-between items-center">
              <span className="font-semibold">Level:</span>
              <Badge value={values.details?.levelName || "Not selected"} severity="success" />
            </div>
          )}
          <div>
            <span className="font-semibold">Name:</span>
            <p className="mt-1">
              {truncate(values.details?.name, { length: 28 }) || "Not specified"}
            </p>
          </div>
          <div>
            <span className="font-semibold">Description:</span>
            <p className="mt-1">{values.details?.description || "Not specified"}</p>
          </div>
        </div>
      </Card>

      {/* Content */}
      <Card className={classNames(themeClasses.bgElevation2, "min-h-52")}>
        <div className="mb-2 flex items-center">
          <h4 className="font-semibold text-xl">
            2. {contentTypeLabel} {isActivityPath ? "Activity Points" : "Content"}
          </h4>
          {validationErrors.content?.hasError && (
            <span className="ml-2">
              <i
                className={`pi pi-info-circle ${themeClasses.iconError}`}
                style={{ fontSize: "1rem" }}
                id="content-validation-error"
              />
              <Tooltip target="#content-validation-error" position="right">
                {validationErrors.content.message}
              </Tooltip>
            </span>
          )}
          <Button
            label="Edit"
            onClick={() => onStepChange(1)}
            link
            size="small"
            className="ml-[10px]"
          />
        </div>
        {isActivityPath ? (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Required Points:</span>
              <Badge
                value={(values.content?.totalPoints ?? 0).toLocaleString()}
                severity="success"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <span className="font-semibold">Selected Lessons:</span>
              <p className="mt-1">
                {values.content?.selectedLessons?.length ?? 0} lessons selected
              </p>
            </div>
            {(values.content?.orderedLessons?.length ?? 0) > 0 && (
              <div>
                <span className="font-semibold">Lesson Order:</span>
                <div className="mt-2 max-h-32 overflow-y-auto">
                  <ol className="list-decimal list-inside body-sm space-y-1">
                    {values.content!.orderedLessons!.map((lesson, index) => (
                      <li key={index}>{lesson.name || `Lesson ${index + 1}`}</li>
                    ))}
                  </ol>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Settings */}
      <Card className={classNames(themeClasses.bgElevation2, "min-h-52")}>
        <div className="mb-2 flex items-center">
          <h4 className="font-semibold text-xl">3. {contentTypeLabel} Settings</h4>
          <Button
            label="Edit"
            onClick={() => onStepChange(2)}
            link
            size="small"
            className="ml-[10px]"
          />
        </div>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="font-semibold">Require Retake:</span>
            <Badge
              value={values.settings?.requireRetake ? "Enabled" : "Disabled"}
              severity={values.settings?.requireRetake ? "warning" : "secondary"}
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="font-semibold">Include Description on Certificate:</span>
            <Badge
              value={values.settings?.includeDescriptionOnCertificate ? "Enabled" : "Disabled"}
              severity={values.settings?.includeDescriptionOnCertificate ? "success" : "secondary"}
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="font-semibold">{contentTypeLabel} Activated:</span>
            <Badge
              value={values.settings?.activatePath ? "Active" : "Inactive"}
              severity={values.settings?.activatePath ? "success" : "danger"}
            />
          </div>
          <div className="flex justify-between items-center">
            <span className="font-semibold">{contentTypeLabel} Restricted:</span>
            <Badge
              value={values.settings?.restrictPath ? "Restricted" : "Open"}
              severity={values.settings?.restrictPath ? "warning" : "info"}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ReviewStep;
